import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Building2 } from "lucide-react";
import { experience, recognition } from "@/data/portfolio";
import { Reveal } from "@/lib/motion";
import { SectionHeader } from "@/components/About";
import { TID } from "@/constants/testIds";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const EASE = [0.16, 1, 0.3, 1];
const TIMELINE_X = "left-[20px] sm:left-[24px]";
const TIMELINE_TOP = "top-5";

function Milestone({ item }) {
  const reduce = useReducedMotion();
  return (
    <div className="relative pl-14 sm:pl-20" data-testid={`journey-milestone-${item.id}`}>
      <motion.div
        initial={reduce ? {} : { opacity: 0, x: 26, filter: "blur(6px)" }}
        whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
        viewport={{ once: true, margin: "-90px" }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
        className="group relative mb-14 overflow-hidden rounded-xl border border-border bg-card/60 p-6 backdrop-blur-md"
      >
        <span className="pointer-events-none absolute left-0 top-0 h-3 w-3 border-l border-t border-primary/60" />
        <span className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b border-r border-primary/60" />
        <div className="mb-2 flex items-center gap-2 font-mono text-[0.68rem] uppercase tracking-[0.16em] text-primary">
          <Building2 className="h-3.5 w-3.5" /> {item.period} · {item.location}
        </div>
        <h3 className="font-display text-xl font-bold text-foreground">{item.role}</h3>
        <div className="mb-4 font-mono text-sm text-muted-foreground">{item.company}</div>
        <ul className="space-y-2">
          {item.achievements.map((a, j) => (
            <li key={j} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {item.stack.map((s) => <span key={s} className="tag">{s}</span>)}
        </div>
      </motion.div>
    </div>
  );
}

function RecognitionMilestone() {
  return (
    <div className="relative pl-14 sm:pl-20" data-testid="journey-milestone-recognition">
      <Reveal className="group relative mb-14 overflow-hidden rounded-xl border border-border bg-card/60 p-6 backdrop-blur-md">
        <span className="pointer-events-none absolute left-0 top-0 h-3 w-3 border-l border-t border-primary/60" />
        <span className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b border-r border-primary/60" />
        
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{recognition.intro}</p>

        <div className="mt-5 space-y-5">
          <div className="space-y-2.5">
            <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">Leadership</h4>
            <ul className="space-y-2.5">
              {recognition.leadership.map((r) => (
                <li key={r.id} className="text-sm leading-relaxed text-muted-foreground">
                  <span className="text-foreground">{r.title}</span>
                  {r.body ? <span> — {r.body}</span> : null}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2.5">
            <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">Award</h4>
            <ul className="space-y-2.5">
              {recognition.awards.map((r) => (
                <li key={r.id} className="text-sm leading-relaxed text-muted-foreground">
                  <span className="text-foreground">{r.title}</span>
                  {r.body ? <span> — {r.body}</span> : null}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2.5">
            <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">Certifications</h4>
            <div className="flex flex-wrap gap-2">
              {recognition.certifications.map((c) => (
                <span key={c.id} className="tag">
                  {c.issuer} · {c.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}

export function Experience() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.55"] });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="experience" data-testid={TID.experience} className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
      <SectionHeader label="04 / experience" title="Experience." />

      <div className="md:hidden">
        <Accordion type="single" collapsible className="space-y-3">
          {experience.map((item) => (
            <AccordionItem key={item.id} value={item.id} className="overflow-hidden rounded-xl border border-border bg-card/60 px-4">
              <AccordionTrigger className="py-4 text-left no-underline hover:no-underline">
                <span className="flex flex-col items-start gap-1">
                  <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-primary">
                    {item.period} · {item.location}
                  </span>
                  <span className="font-display text-lg font-bold text-foreground">{item.role}</span>
                  <span className="font-mono text-sm text-muted-foreground">{item.company}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-4">
                  <ul className="space-y-2">
                    {item.achievements.map((a, j) => (
                      <li key={j} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-1.5">
                    {item.stack.map((s) => <span key={s} className="tag">{s}</span>)}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}

          <AccordionItem value="recognition" className="overflow-hidden rounded-xl border border-border bg-card/60 px-4">
            <AccordionTrigger className="py-4 text-left no-underline hover:no-underline">
              <span className="flex flex-col items-start gap-1">
                <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-primary">
                  Community &amp; Recognition
                </span>
                <span className="font-display text-lg font-bold text-foreground">Leadership, awards &amp; certifications</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {recognition.intro}
                </p>

                <div className="space-y-4">
                  <div className="space-y-2.5">
                    <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">Leadership</h4>
                    <ul className="space-y-2.5">
                      {recognition.leadership.map((r) => (
                        <li key={r.id} className="text-sm leading-relaxed text-muted-foreground">
                          <span className="text-foreground">{r.title}</span>
                          {r.body ? <span> — {r.body}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2.5">
                    <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">Award</h4>
                    <ul className="space-y-2.5">
                      {recognition.awards.map((r) => (
                        <li key={r.id} className="text-sm leading-relaxed text-muted-foreground">
                          <span className="text-foreground">{r.title}</span>
                          {r.body ? <span> — {r.body}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2.5">
                    <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">Certifications</h4>
                    <div className="flex flex-wrap gap-2">
                      {recognition.certifications.map((c) => (
                        <span key={c.id} className="tag">
                          {c.issuer} · {c.title}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div ref={ref} className="relative hidden md:block">
        <div className={`absolute ${TIMELINE_X} top-0 h-full w-px bg-border`} />
        <motion.div
          className={`absolute ${TIMELINE_X} top-0 h-full w-px origin-top bg-gradient-to-b from-primary via-primary to-primary/0`}
          style={{ scaleY: reduce ? 1 : scaleY }}
        />

        <div className="relative pt-2">
          {experience.map((e) => (
            <Milestone key={e.id} item={e} />
          ))}
          <RecognitionMilestone />
        </div>
      </div>
    </section>
  );
}
